import HiddenInput from "@/components/form/components/HiddenInput";
import { formatCron } from "@/utils/formatUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateJob } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { JobSettings, UpdateJobSettings } from "../../types";

interface Props {
  job?: JobSettings;
  onFormCancel: () => void;
}

export const UpdateJobForm = ({ job, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<UpdateJobSettings>();
  const cron = Form.useWatch("cron", form);

  const updateJob = useUpdateJob({ globalSpinner: false });

  useEffect(() => {
    if (job) {
      form.setFieldsValue(job);
    }
  }, [job, form]);

  const handleFormSubmit = () => {
    if (job) {
      form
        .validateFields()
        .then((values) =>
          updateJob.mutate(
            { id: job.id, object: values },
            { onSuccess: onFormCancel, onError: (error) => setErrorsToForm(form, "job.attrs", error.violations) },
          ),
        )
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={!!job}
      title={t("job.titles.updateJob")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateJob.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="updateJobForm">
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name="enabled" />
        <HiddenInput name="preferences" />

        <Row gutter={rowGutter} justify="center">
          <Col span={24}>
            <Form.Item name="name" label={t("job.attrs.name")} rules={[validations.notBlank, validations.size(1, 128)]}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="cron"
              label={`${t("job.attrs.cron")}${cron ? ` - ${formatCron(cron)}` : ""}`}
              rules={[validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
